import type { ChipOption } from '../../components/OptionChips';
import type {
  CaptionLength,
  CaptionLengthRange,
  CaptionOptionsRead,
  CaptionStyle,
  PlatformName,
} from '../../types/api';

/**
 * Reading and offering the caption Length and Style controls.
 *
 * Plain functions over the registry the server answered with (no React, no
 * API), so the brief and the review spec sheet say the same thing about the
 * same keys. The ranges are never spelled out here: they are the server's.
 *
 * The keys themselves are the closed wire vocabulary (`CaptionLength`,
 * `CaptionStyle`), which is why the chips can still be offered, with plain
 * fallback labels, when the registry cannot be read.
 */

/** The chip value that stands for "the model decides". Sent as null. */
export const CAPTION_AUTO = 'auto';

const LENGTH_KEYS: readonly CaptionLength[] = ['short', 'medium', 'expanded'];
const STYLE_KEYS: readonly CaptionStyle[] = ['formatted', 'human'];

/** Used only when the registry is unavailable. The server's label wins otherwise. */
const LENGTH_FALLBACK_LABEL: Readonly<Record<CaptionLength, string>> = {
  short: 'Short',
  medium: 'Medium',
  expanded: 'Expanded',
};

const STYLE_FALLBACK_LABEL: Readonly<Record<CaptionStyle, string>> = {
  formatted: 'Formatted',
  human: 'Human',
};

/** A length key, or null for anything else: drafts and router state are untrusted. */
export function readCaptionLength(value: unknown): CaptionLength | null {
  return LENGTH_KEYS.find((key) => key === value) ?? null;
}

/** A style key, or null for anything else. Same rule as the length. */
export function readCaptionStyle(value: unknown): CaptionStyle | null {
  return STYLE_KEYS.find((key) => key === value) ?? null;
}

/** "150 to 400 characters". */
export function formatLengthRange(range: CaptionLengthRange): string {
  return `${range.min.toLocaleString()} to ${range.max.toLocaleString()} characters`;
}

/** The range this length means on `platform`, or null when the registry has none. */
export function captionLengthRange(
  key: CaptionLength,
  platform: PlatformName,
  options: CaptionOptionsRead | null,
): CaptionLengthRange | null {
  return options?.lengths.find((length) => length.key === key)?.ranges[platform] ?? null;
}

export function captionLengthLabel(key: CaptionLength, options: CaptionOptionsRead | null): string {
  return options?.lengths.find((length) => length.key === key)?.label ?? LENGTH_FALLBACK_LABEL[key];
}

export function captionStyleLabel(key: CaptionStyle, options: CaptionOptionsRead | null): string {
  return options?.styles.find((style) => style.key === key)?.label ?? STYLE_FALLBACK_LABEL[key];
}

/** The server's blurb for a length, or null without a registry. */
export function captionLengthBlurb(
  key: CaptionLength,
  options: CaptionOptionsRead | null,
): string | null {
  return options?.lengths.find((length) => length.key === key)?.blurb ?? null;
}

/** The server's blurb for a style, or null without a registry. */
export function captionStyleBlurb(
  key: CaptionStyle,
  options: CaptionOptionsRead | null,
): string | null {
  return options?.styles.find((style) => style.key === key)?.blurb ?? null;
}

/**
 * Auto, Short, Medium, Expanded, each carrying this platform's range when the
 * registry has one. Without the registry the chips go out bare, and still work.
 */
export function lengthChipOptions(
  platform: PlatformName,
  options: CaptionOptionsRead | null,
): ChipOption[] {
  return [
    { value: CAPTION_AUTO, label: 'Auto' },
    ...LENGTH_KEYS.map((key): ChipOption => {
      const range = captionLengthRange(key, platform, options);
      const blurb = captionLengthBlurb(key, options);
      return {
        value: key,
        label: captionLengthLabel(key, options),
        ...(range ? { detail: formatLengthRange(range) } : {}),
        ...(blurb ? { title: blurb } : {}),
      };
    }),
  ];
}

/** Auto, Formatted, Human, with the blurb as each chip's tooltip when known. */
export function styleChipOptions(options: CaptionOptionsRead | null): ChipOption[] {
  return [
    { value: CAPTION_AUTO, label: 'Auto' },
    ...STYLE_KEYS.map((key): ChipOption => {
      const blurb = captionStyleBlurb(key, options);
      return {
        value: key,
        label: captionStyleLabel(key, options),
        ...(blurb ? { title: blurb } : {}),
      };
    }),
  ];
}

/**
 * What the review spec sheet says for a length: "Short, 150 to 400
 * characters", or the bare label when the range is not on hand.
 */
export function captionLengthSummary(
  key: CaptionLength,
  platform: PlatformName,
  options: CaptionOptionsRead | null,
): string {
  const label = captionLengthLabel(key, options);
  const range = captionLengthRange(key, platform, options);
  return range ? `${label}, ${formatLengthRange(range)}` : label;
}
